import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export interface BrandStory {
  id: string;
  title: string;
  description: string;
  author: string;
  role: string;
  image_url?: string;
  background_image_url?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface BrandStoryFormData {
  title: string;
  description?: string;
  author: string;
  role: string;
  image_url?: string;
  background_image_url?: string;
  is_active: boolean;
  sort_order: number;
}

export const useBrandStories = () => {
  return useQuery({
    queryKey: ['brand-stories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brand_stories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as BrandStory[];
    },
  });
};

export const useAllBrandStories = () => {
  return useQuery({
    queryKey: ['all-brand-stories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('brand_stories')
        .select('*')
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data as BrandStory[];
    },
  });
};

export const useCreateBrandStory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BrandStoryFormData) => {
      const { data: result, error } = await supabase
        .from('brand_stories')
        .insert(data)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-stories'] });
      queryClient.invalidateQueries({ queryKey: ['all-brand-stories'] });
      toast({
        title: 'Success',
        description: 'Brand story created successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to create brand story',
        variant: 'destructive',
      });
      console.error('Error creating brand story:', error);
    },
  });
};

export const useUpdateBrandStory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<BrandStoryFormData> }) => {
      const { data: result, error } = await supabase
        .from('brand_stories')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-stories'] });
      queryClient.invalidateQueries({ queryKey: ['all-brand-stories'] });
      toast({
        title: 'Success',
        description: 'Brand story updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to update brand story',
        variant: 'destructive',
      });
      console.error('Error updating brand story:', error);
    },
  });
};

export const useDeleteBrandStory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('brand_stories')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-stories'] });
      queryClient.invalidateQueries({ queryKey: ['all-brand-stories'] });
      toast({
        title: 'Success',
        description: 'Brand story deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to delete brand story',
        variant: 'destructive',
      });
      console.error('Error deleting brand story:', error);
    },
  });
};

export const useReorderBrandStories = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: Array<{ id: string; sort_order: number }>) => {
      const updates = items.map(item =>
        supabase
          .from('brand_stories')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id)
      );

      const results = await Promise.all(updates);
      const errors = results.filter(result => result.error);
      
      if (errors.length > 0) {
        throw errors[0].error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brand-stories'] });
      queryClient.invalidateQueries({ queryKey: ['all-brand-stories'] });
      toast({
        title: 'Success',
        description: 'Brand stories reordered successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: 'Failed to reorder brand stories',
        variant: 'destructive',
      });
      console.error('Error reordering brand stories:', error);
    },
  });
};